<template>
  <section class="blog-section">
    <div class="container">
      <div class="section-header">
        <h2>{{ $t('homePage.blog.title') }}</h2>
        <p>{{ $t('homePage.blog.subtitle') }}</p>
      </div>

      <div v-if="blogsLoading" class="loading-message">
        {{ $t('homePage.blog.loading') }}
      </div>

      <div v-else-if="blogsError" class="error-message">
        {{ $t('homePage.blog.error') }}
      </div>

      <div v-else class="blog-grid">
        <div v-for="blog in blogs" :key="blog.id" class="blog-card">
          <div class="blog-image-wrapper">
            <img :src="blog.image" :alt="blog.title" class="blog-image" />
            <div class="blog-date">{{ formatDate(blog.date) }}</div>
          </div>
          <div class="blog-content">
            <h3>{{ blog.title }}</h3>
            <p>{{ blog.excerpt }}</p>
            <a
              :href="`https://dashboard.wholelifegoals.com/blog/${blog.slug}`"
              target="_blank"
              rel="noopener noreferrer"
              class="read-more"
            >
              {{ $t('homePage.blog.readMore') }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/* eslint-disable */
import axios from 'axios';

export default {
  name: 'BlogSection',
  data() {
    return {
      blogs: [],
      blogsLoading: false,
      blogsError: null
    };
  },
  computed: {
    fallbackBlogs() {
      return [
        {
          id: 1,
          title: this.$t('homePage.blog.fallback.0.title'),
          slug: 'setting-smart-goals',
          date: new Date('2024-03-15'),
          excerpt: this.$t('homePage.blog.fallback.0.excerpt'),
          image: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=500&h=300&fit=crop'
        },
        {
          id: 2,
          title: this.$t('homePage.blog.fallback.1.title'),
          slug: 'wellness-professionals',
          date: new Date('2024-03-10'),
          excerpt: this.$t('homePage.blog.fallback.1.excerpt'),
          image: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=500&h=300&fit=crop'
        },
        {
          id: 3,
          title: this.$t('homePage.blog.fallback.2.title'),
          slug: 'wellness-myths',
          date: new Date('2024-03-05'),
          excerpt: this.$t('homePage.blog.fallback.2.excerpt'),
          image: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=500&h=300&fit=crop'
        }
      ];
    }
  },
  mounted() {
    this.fetchBlogPosts();
  },
  methods: {
    async fetchBlogPosts() {
      this.blogsLoading = true;
      this.blogsError = null;
      try {
        const response = await axios.get('https://dashboard.wholelifegoals.com/wp-json/wp/v2/posts', {
          params: {
            per_page: 3,
            orderby: 'date',
            order: 'desc',
            _embed: true
          }
        });

        this.blogs = response.data.map(post => ({
          id: post.id,
          title: post.title.rendered,
          slug: post.slug,
          date: new Date(post.date),
          excerpt: this.extractText(post.excerpt.rendered) || this.extractText(post.content.rendered).substring(0, 150),
          image: this.getFeaturedImage(post) || this.fallbackBlogs[0].image
        }));
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        this.blogsError = this.$t('homePage.blog.error');
        this.blogs = this.fallbackBlogs;
      } finally {
        this.blogsLoading = false;
      }
    },

    getFeaturedImage(post) {
      return post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
    },

    extractText(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return (div.textContent || div.innerText || '').trim();
    },

    formatDate(date) {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/_variables.scss';

.blog-section {
  padding: 5rem 0;
  background: $bg-light;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.1rem;
    color: $text-light;
    max-width: 500px;
    margin: 0 auto;
  }
}

.loading-message {
  text-align: center;
  padding: 3rem 0;
  color: $text-light;
  font-size: 1.1rem;
}

.error-message {
  text-align: center;
  padding: 2rem;
  background: #fee;
  color: #c33;
  border-radius: 8px;
  margin-top: 2rem;
}

.blog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;
}

.blog-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12);

    .blog-image {
      transform: scale(1.05);
    }
  }
}

.blog-image-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.blog-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.blog-date {
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
}

.blog-content {
  padding: 1.75rem;

  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.75rem;
    line-height: 1.4;
  }

  p {
    color: $text-light;
    font-size: 0.95rem;
    margin-bottom: 1.25rem;
    line-height: 1.6;
  }
}

.read-more {
  color: $brand-color;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateX(4px);
  }
}
</style>
